import * as React from 'react';
import {connect} from 'react-redux';

import {useMutation} from '@apollo/react-hooks';
import type Moment from 'moment';

import {Session} from 'nutshell-core/session';
import {EmailTemplate} from 'nutshell-core/settings-constants';
import type {User} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Dialog} from 'shells/dialog/dialog';
import {getUserFacingNoun} from 'shells/edition/get-user-facing-noun';
import {Stack} from 'shells/layout/stack/stack';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {FooterBar} from 'shells/modal/footer-bar';
import {PrepareToSendBreadcrumbs} from 'shells/prepare-to-send/prepare-to-send-breadcrumbs';
import {PrepareToSendButtons} from 'shells/prepare-to-send/prepare-to-send-buttons';
import {PrepareToSendWrapper} from 'shells/prepare-to-send/prepare-to-send-wrapper';
import {roundTimeToNextQuarter} from 'shells/select-time-picker/helpers';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {Body} from 'shells/typography/body';

import type {CampaignType, EntityType} from 'nutshell-graphql-types';
import type {
    EditionFragment as Edition,
    SendEdition as SendEditionMutation,
    SendEditionVariables as SendEditionMutationVariables,
    SendEditionToFilteredList as SendEditionToFilteredListMutation,
    SendEditionToFilteredListVariables as SendEditionToFilteredListMutationVariables,
} from 'nutshell-graphql-types';

import {Setting} from '../../../core/setting';

import type {NutshellState} from '../../../../store';

// @ts-expect-error
import {useGetUsageStats} from '../../graphql/hooks';

import type {ModalComponent} from '../edition-page';
import SEND_EMAIL_EDITION_TO_FILTERED_LIST from '../prepare-to-send-modal/graphql/mutations/send-edition-to-filtered-list.graphql';
import SEND_EMAIL_EDITION from '../prepare-to-send-modal/graphql/mutations/send-email-marketing-edition.graphql';

import {
    getIssueCount,
    getSelectedRecipientsFromAudience,
    getEditionSecurityInfo,
    getError,
    shouldPrepareViewButtonsBeDisabled,
    isTooShortForABTesting,
    shouldAudienceSelectionViewButtonsBeDisabled,
    getAudienceUnsubscribedRate,
    getBusinessHoursStart,
    getAudienceError,
} from './helpers';
import {NewPrepareToSendView} from './new-prepare-to-send-view';
import {NewPreviewView} from './new-preview-view';
import {
    NewRecipientsSelectionView,
    RECIPIENT_TYPE_AUDIENCE,
    RECIPIENT_TYPE_SAVED_LIST,
} from './new-recipients-selection-view';
import type {SelectedRecipients} from './new-recipients-selection-view';
import {NewScheduleView} from './new-schedule-view';

export const LIMITED_CAMPAIGN_USER_DEFAULT_RECIPIENTS = {type: RECIPIENT_TYPE_SAVED_LIST} as const;

function mapStateToProps(state: NutshellState): StateProps {
    return {
        user: Session.getSessionStateUser(state),
    };
}

type OwnProps = {
    onClose: (
        editorTab?: string | null | undefined,
        onboardingModalComponent?: ModalComponent
    ) => void;
    edition: Edition;
    isEditionLoading: boolean;
    onSendSuccess: () => void;
    type: CampaignType;
    isScheduling: boolean;
    isRescheduling: boolean;
    setIsScheduling: (arg1: boolean) => void;
    setIsRescheduling: (arg1: boolean) => void;
    onUnschedule?: () => Promise<any>;
};

type ConnectedProps = OwnProps & {
    onSendToAudience: (audienceId: string) => Promise<any>;
    onSendToFilteredList: (arg1: {
        filterString: string;
        entityType: EntityType;
        savedFilterId?: string | null | undefined;
        // @ts-expect-error
        scheduledTime?: Moment | null | undefined;
    }) => Promise<any>;
    // @ts-expect-error
    onScheduleToAudience: (arg1: {audienceId: string; scheduledTime: Moment}) => Promise<any>;
    onScheduleToFilteredList: (arg1: {
        filterString: string;
        entityType: EntityType;
        savedFilterId?: string | null | undefined;
        // @ts-expect-error
        scheduledTime?: Moment | null | undefined;
    }) => Promise<any>;
    sendError: string | null | undefined;
};

type StateProps = {
    user: User | null | undefined;
};

type Props = OwnProps & ConnectedProps & StateProps;

function PrepareToSendPageComponent(props: Props) {
    const [isShowingSendConfirmation, setIsShowingSendConfirmation] =
        React.useState<boolean>(false);
    const [sendEmailButtonRef, setSendEmailButtonRef] = React.useState<
        HTMLElement | null | undefined
    >(null);
    const [isSending, setIsSending] = React.useState<boolean>(false);
    // @ts-expect-error
    const [scheduledDate, setScheduledDate] = React.useState<Moment>(
        roundTimeToNextQuarter(getBusinessHoursStart())
    );
    const [currentView, setCurrentView] = React.useState<string>('prepare');
    const canAccessFullCampaigns = Boolean(
        props.user && props.user.permissions.canAccessFullCampaigns
    );
    const defaultSelectedRecipients = canAccessFullCampaigns
        ? null
        : LIMITED_CAMPAIGN_USER_DEFAULT_RECIPIENTS;
    const [selectedRecipients, setSelectedRecipients] = React.useState<
        SelectedRecipients | null | undefined
    >(
        props.edition.audience
            ? getSelectedRecipientsFromAudience(props.edition.audience)
            : defaultSelectedRecipients
    );
    const {nutshellMarketingStats} = useGetUsageStats();

    /*
    Our breadcrumbs on this page will update with the scheduled date, to say
    `sending on $DATE`. We don't want to show this scheduled time until the user
    has visited this page, however. I tried to add this functionality by making
    the scheduled time nullable, but many parts of the schedule page require a
    time value, making it very difficult to make that value null. Instead, I'm
    going to add this state and simply hide the value on the breadcrumbs until
    the schedule page is visited.
    */
    const [visitedSchedulePage, setVisitedSchedulePage] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (props.isRescheduling) {
            setCurrentView('schedule');
        }
    }, [props.isRescheduling]);

    const onCloseModal = (editorTab?: string | null, modalComponent?: ModalComponent) => {
        setIsShowingSendConfirmation(false);
        setIsSending(false);
        props.onClose(editorTab, modalComponent);
    };

    const recipientCount =
        selectedRecipients && selectedRecipients.recipientSelection
            ? selectedRecipients.recipientSelection.recipientCount
            : null;

    const audienceUnsubcribedRate = getAudienceUnsubscribedRate(selectedRecipients);

    const userFacingNoun = getUserFacingNoun(props.type);

    const editionSecurityInfo = props.edition.from
        ? getEditionSecurityInfo(props.edition.from)
        : null;

    const listSizeWarning = (): React.ReactNode | null | undefined => {
        if (selectedRecipients && recipientCount === 0) {
            return (
                <Body color='error'>
                    This {selectedRecipients.type === RECIPIENT_TYPE_AUDIENCE ? 'audience' : 'list'}{' '}
                    has no members
                </Body>
            );
        }
    };

    const isOverRecipientLimit =
        typeof recipientCount === 'number' &&
        typeof props.edition.recipientLimitForDeliverability === 'number' &&
        recipientCount > props.edition.recipientLimitForDeliverability;

    return (
        <Setting settingName={EmailTemplate.STREET_ADDRESS_SETTING_NAME}>
            {({setting}) => {
                const issueCount = getIssueCount(props.edition, setting);

                const audienceError = getAudienceError(selectedRecipients, nutshellMarketingStats);

                const {errorMessage, shouldDisable, showUpgradeBanner} = getError(
                    props.edition.gateError,
                    audienceError,
                    currentView,
                    props.sendError
                );

                const arePrepareViewButtonsDisabled = shouldPrepareViewButtonsBeDisabled(
                    issueCount,
                    errorMessage,
                    props.isEditionLoading,
                    editionSecurityInfo
                );

                const isTooShortForABTestingCheck = isTooShortForABTesting(
                    props.edition.isABTesting,
                    recipientCount,
                    currentView
                );

                const isValidRecipientSelection = Boolean(
                    recipientCount && recipientCount > 0 && !isTooShortForABTestingCheck
                );

                const audienceSelectionViewButtonsDisabled =
                    shouldAudienceSelectionViewButtonsBeDisabled(
                        isValidRecipientSelection,
                        errorMessage
                    );

                const scheduleViewButtonsDisabled = !scheduledDate;

                const pages = [
                    {
                        id: 'prepare',
                        component: (
                            <NewPrepareToSendView
                                edition={props.edition}
                                streetAddress={setting ? setting.value : null}
                                closeAndMoveToView={(
                                    editorTab: string,
                                    modalComponent?: ModalComponent
                                ) => {
                                    onCloseModal(editorTab, modalComponent);
                                }}
                                issueCount={issueCount}
                                type={props.type}
                                isLoading={props.isEditionLoading}
                                recipientCount={recipientCount}
                                audienceUnsubcribedRate={audienceUnsubcribedRate}
                                editionSecurityInfo={editionSecurityInfo}
                            />
                        ),
                    },
                    {
                        id: 'recipients-selection',
                        component: (
                            <NewRecipientsSelectionView
                                selectedRecipients={selectedRecipients}
                                setSelectedRecipients={setSelectedRecipients}
                                canAccessFullCampaigns={canAccessFullCampaigns}
                            />
                        ),
                    },
                    {
                        id: 'schedule',
                        component: (
                            <NewScheduleView
                                scheduledDate={scheduledDate}
                                setScheduledDate={setScheduledDate}
                                onReviewAndSend={() => {
                                    // clear out scheduling states
                                    props.setIsScheduling(false);
                                    props.setIsRescheduling(false);
                                    setCurrentView('review');
                                }}
                                type={props.type}
                                onUnschedule={
                                    props.edition.scheduledTime && props.onUnschedule
                                        ? () =>
                                              // @ts-expect-error
                                              props.onUnschedule().then(() => {
                                                  // clear out scheduling states
                                                  props.setIsScheduling(false);
                                                  props.setIsRescheduling(false);
                                                  onCloseModal();
                                              })
                                        : undefined
                                }
                                onLoad={() => {
                                    setVisitedSchedulePage(true);
                                }}
                            />
                        ),
                    },
                    {
                        id: 'review',
                        component: (
                            <NewPreviewView
                                edition={props.edition}
                                scheduledDate={scheduledDate ? scheduledDate : undefined}
                                type={props.type}
                                selectedRecipients={selectedRecipients}
                                isScheduling={props.isScheduling || props.isRescheduling}
                            />
                        ),
                    },
                ];

                const sendToText =
                    selectedRecipients && selectedRecipients.recipientSelection
                        ? selectedRecipients.recipientSelection.name
                        : undefined;

                return (
                    <>
                        <PrepareToSendWrapper
                            errorMessage={errorMessage}
                            shouldDisable={shouldDisable}
                            showUpgradeBanner={showUpgradeBanner}
                            isLoading={props.isEditionLoading}
                            // @ts-expect-error
                            content={<TabbedContent selectedTab={currentView} tabOptions={pages} />}
                            userFacingNoun={userFacingNoun}
                            isFirstPage={currentView === 'prepare'}
                            onClose={() => {
                                props.setIsScheduling(false);
                                props.setIsRescheduling(false);
                                onCloseModal();
                            }}
                            footerBar={
                                <FooterBar
                                    left={
                                        <PrepareToSendBreadcrumbs
                                            prepareViewButtonsDisabled={
                                                arePrepareViewButtonsDisabled
                                            }
                                            audienceSelectionViewButtonsDisabled={
                                                audienceSelectionViewButtonsDisabled
                                            }
                                            scheduleViewButtonsDisabled={
                                                scheduleViewButtonsDisabled
                                            }
                                            currentView={currentView}
                                            setCurrentView={(view: string) => setCurrentView(view)}
                                            // scheduling and rescheduling do the same thing to the breadcrumbs
                                            isScheduling={
                                                props.isScheduling || props.isRescheduling
                                            }
                                            sendToText={sendToText}
                                            scheduledDate={
                                                visitedSchedulePage
                                                    ? scheduledDate.format(
                                                          FormattedTimestamp.formats
                                                              .longMonthDayYearWithTime
                                                      )
                                                    : undefined
                                            }
                                        />
                                    }
                                    right={
                                        // @ts-expect-error
                                        <Stack isHorizontal={true} alignCenter={true} spacing={16}>
                                            {isOverRecipientLimit && (
                                                <Body color='error'>
                                                    Configure{' '}
                                                    <a
                                                        href='https://support.nutshell.com/en/articles/8428873-campaigns-how-to-set-up-dkim-spf-and-dmarc'
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        className='underline'
                                                    >
                                                        DMARC
                                                    </a>{' '}
                                                    to send content to large audiences
                                                </Body>
                                            )}
                                            <PrepareToSendButtons
                                                prepareViewButtonsDisabled={
                                                    arePrepareViewButtonsDisabled
                                                }
                                                audienceSelectionViewButtonsDisabled={
                                                    audienceSelectionViewButtonsDisabled
                                                }
                                                scheduleViewButtonsDisabled={
                                                    scheduleViewButtonsDisabled
                                                }
                                                saveButtonDisabled={Boolean(
                                                    !isValidRecipientSelection ||
                                                        !props.edition.from ||
                                                        (props.edition.from &&
                                                            props.edition.from.status !==
                                                                'VERIFIED') ||
                                                        issueCount > 0 ||
                                                        props.isEditionLoading ||
                                                        isOverRecipientLimit
                                                )}
                                                currentView={currentView}
                                                setCurrentView={(view: string) =>
                                                    setCurrentView(view)
                                                }
                                                onSchedule={() => {
                                                    if (!selectedRecipients) {
                                                        return;
                                                    }

                                                    if (
                                                        selectedRecipients.type ===
                                                            RECIPIENT_TYPE_AUDIENCE &&
                                                        selectedRecipients.recipientSelection
                                                    ) {
                                                        props
                                                            .onScheduleToAudience({
                                                                audienceId:
                                                                    selectedRecipients
                                                                        .recipientSelection.id,
                                                                scheduledTime: scheduledDate,
                                                            })
                                                            .then(() => {
                                                                setIsSending(false);
                                                            });
                                                    } else if (
                                                        selectedRecipients.type ===
                                                            RECIPIENT_TYPE_SAVED_LIST &&
                                                        selectedRecipients.recipientSelection
                                                    ) {
                                                        props
                                                            .onScheduleToFilteredList({
                                                                filterString:
                                                                    selectedRecipients
                                                                        .recipientSelection
                                                                        .filterString,
                                                                entityType:
                                                                    selectedRecipients
                                                                        .recipientSelection
                                                                        .entityType,
                                                                savedFilterId:
                                                                    selectedRecipients
                                                                        .recipientSelection.id,
                                                                scheduledTime: scheduledDate,
                                                            })
                                                            .then(() => {
                                                                setIsSending(false);
                                                            });
                                                    }
                                                }}
                                                setIsSending={(value) => setIsSending(value)}
                                                setIsShowingSendConfirmation={(value) =>
                                                    setIsShowingSendConfirmation(value)
                                                }
                                                setSendEmailButtonRef={(ref) =>
                                                    setSendEmailButtonRef(ref)
                                                }
                                                scheduledDate={scheduledDate}
                                                recipientCount={recipientCount}
                                                onCloseModal={() => onCloseModal()}
                                                isSending={isSending}
                                                // scheduling and rescheduling do the same thing to the buttons
                                                isScheduling={
                                                    props.isScheduling || props.isRescheduling
                                                }
                                                isAllowedToSchedule={!isOverRecipientLimit}
                                                setIsScheduling={props.setIsScheduling}
                                                getListSizeWarning={listSizeWarning}
                                            />
                                        </Stack>
                                    }
                                />
                            }
                        />
                        {isShowingSendConfirmation && (
                            <Dialog
                                anchor={sendEmailButtonRef}
                                question={
                                    typeof recipientCount === 'number'
                                        ? `This message will be sent to ${recipientCount}
                                       ${recipientCount !== 1 ? 'recipients' : 'recipient'}.
                                        Are you sure you'd like to send this now?`
                                        : // This case shouldn't ever happen - but default fallback message
                                          "This cannot be undone, are you sure you'd like to send this now?"
                                }
                                primaryText='Yes, send now'
                                onPrimary={() => {
                                    setIsSending(true);
                                    setIsShowingSendConfirmation(false);
                                    if (!selectedRecipients) {
                                        return;
                                    }

                                    if (
                                        selectedRecipients.type === RECIPIENT_TYPE_AUDIENCE &&
                                        selectedRecipients.recipientSelection
                                    ) {
                                        props
                                            .onSendToAudience(
                                                selectedRecipients.recipientSelection.id
                                            )
                                            .then(() => {
                                                setIsSending(false);
                                            });
                                    } else if (
                                        selectedRecipients.type === RECIPIENT_TYPE_SAVED_LIST &&
                                        selectedRecipients.recipientSelection
                                    ) {
                                        props
                                            .onSendToFilteredList({
                                                filterString:
                                                    selectedRecipients.recipientSelection
                                                        .filterString,
                                                entityType:
                                                    selectedRecipients.recipientSelection
                                                        .entityType,
                                                savedFilterId:
                                                    selectedRecipients.recipientSelection.id,
                                            })
                                            .then(() => {
                                                setIsSending(false);
                                            });
                                    }
                                }}
                                secondaryText='No, don’t send'
                                onSecondary={() => {
                                    setIsShowingSendConfirmation(false);
                                }}
                            />
                        )}
                    </>
                );
            }}
        </Setting>
    );
}

// @ts-expect-error
const connector = connect<ConnectedProps, OwnProps, _, _, _, _>(mapStateToProps);
const PrepareToSendPageComponentConnected = connector(PrepareToSendPageComponent);

export function PrepareToSendPage(props: OwnProps) {
    const [sendEdition, {error: sendEditionError}] = useMutation<
        SendEditionMutation,
        SendEditionMutationVariables
    >(SEND_EMAIL_EDITION);

    const [sendEditionToFilteredList, {error: sendEditionToFilteredListError}] = useMutation<
        SendEditionToFilteredListMutation,
        SendEditionToFilteredListMutationVariables
    >(SEND_EMAIL_EDITION_TO_FILTERED_LIST);

    const sendError =
        // @ts-expect-error
        safelyGetGraphQLErrorMessage(sendEditionError) ||
        // @ts-expect-error
        safelyGetGraphQLErrorMessage(sendEditionToFilteredListError);

    return (
        <PrepareToSendPageComponentConnected
            {...props}
            sendError={sendError}
            // @ts-expect-error
            onSendToAudience={(audienceId) => {
                return sendEdition({
                    variables: {
                        edition: {editionId: props.edition.id, audienceId},
                    },
                }).then(() => {
                    props.onSendSuccess();
                });
            }}
            onSendToFilteredList={(input: {
                filterString: string;
                entityType: EntityType;
                savedFilterId?: string | null | undefined;
                // @ts-expect-error
                scheduledTime?: Moment | null | undefined;
            }) => {
                return sendEditionToFilteredList({
                    variables: {
                        input: {
                            editionId: props.edition.id,
                            filterString: input.filterString,
                            savedFilterId: input.savedFilterId,
                            entityType: input.entityType,
                            scheduledTime: input.scheduledTime ? input.scheduledTime.unix() : null,
                        },
                    },
                }).then(() => {
                    props.onSendSuccess();
                });
            }}
            // @ts-expect-error
            onScheduleToAudience={(input: {audienceId: string; scheduledTime: Moment}) => {
                return sendEdition({
                    variables: {
                        edition: {
                            editionId: props.edition.id,
                            audienceId: input.audienceId,
                            scheduledTime: input.scheduledTime.unix(),
                        },
                    },
                });
            }}
            onScheduleToFilteredList={(input: {
                filterString: string;
                entityType: EntityType;
                savedFilterId?: string | null | undefined;
                // @ts-expect-error
                scheduledTime?: Moment | null | undefined;
            }) => {
                return sendEditionToFilteredList({
                    variables: {
                        input: {
                            editionId: props.edition.id,
                            filterString: input.filterString,
                            savedFilterId: input.savedFilterId,
                            entityType: input.entityType,
                            scheduledTime: input.scheduledTime ? input.scheduledTime.unix() : null,
                        },
                    },
                });
            }}
        />
    );
}
